import styles from './Seller.module.css';

import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from "@mui/material/Button";
import Edit from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Check from '@mui/icons-material/Check';

import React from "react";

import * as api from "../../api.js";

import { useAuth } from '../../AuthContext';

export const Seller = ({ seller }) => {
    const [ error, setError ] = React.useState(null);
    const [ user, setUser ] = React.useState(seller);
    const [ requiredFields, setRequiredFields ] = React.useState([]);
    const [ editable, setEditable ] = React.useState(false);
    const [ editAction, setEditAction ] = React.useState('cancel');
    const [ phoneIsValid, setPhoneIsValid ] = React.useState(false);

    const { auth, claims, token } = useAuth();

    const userIsAdmin = !!claims?.admin;
    const userIsSelf = auth.currentUser.uid === seller.uid;
    const userPrivacyInfo = seller.privacy;

    const validatePhoneNumber = (phoneNumber) => {
        if (!phoneNumber || phoneNumber.trim() === '') {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Phone number is required.');

            setPhoneIsValid(false);

            return false;
        }

        const phonePlusOne = /^\+1/.test(phoneNumber);
        const fullNumber = phonePlusOne ? phoneNumber : `+1${ phoneNumber }`;
        const isValid = /^(\+1)\d{10}$/.test(fullNumber);

        if (isValid) {
            setRequiredFields(prev => prev.filter(field => field !== 'phoneNumber'));
            setError(null);
            setUser(prev => ({ ...prev, phoneNumber: fullNumber }));

            setPhoneIsValid(true);

            return true;
        } else {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Invalid phone number format. Must be 10 digits.');

            setPhoneIsValid(false);

            return false;
        }
    };

    // MARK: Input Handlers
    const handleOnChange = (e) => {
        const { name, value, type, checked, required } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setUser(prev => ({ ...prev, [ name ]: fieldValue }));

        if (name === 'phoneNumber') {
            const isValid = validatePhoneNumber(fieldValue);

            if (!isValid) {
                return;
            }
        }

        // Handle required field validation
        if (required && !fieldValue) {
            setRequiredFields(prev => [ ...prev.filter(field => field !== name), name ]);
            setError(`${ name } is required.`);

            return;
        }

        // Clear field from required list if now valid
        setRequiredFields(prev => prev.filter(field => field !== name));
        setEditAction('save');
        setError(null);
    };

    // MARK: Edit handler
    const handleOnEdit = async () => {
        if (editable && editAction === 'save') {
            if (requiredFields.length > 0) {
                setError(`The following fields are required: ${ requiredFields.join(', ') }`);

                return;
            }

            const saved = await api.updateUser(user, token);

            if (saved.error) {
                setError(saved.error);
            } else {
                // Fetch fresh data from backend to ensure parent has latest state
                const updatedUser = await api.getUser(user.uid, token);

                if (updatedUser.error) {
                    setError(updatedUser.error);

                    return;
                }

                // Update local state with fresh backend data
                setUser(updatedUser);

                setEditable(false);
            }
        } else if (editable && editAction === 'cancel') {
            setEditable(false);
        } else {
            setEditable(true);
            setEditAction('cancel');
        }
    };

    // MARK: Cancel handler
    const handleOnCancel = async () => {
        setEditable(false);
    };

    // MARK: admin & required effect
    React.useEffect(() => {
        const missing = [];

        if (!user.firstName) missing.push('firstName');
        if (!user.lastName) missing.push('lastName');
        if (!user.phoneNumber) missing.push('phoneNumber');

        setRequiredFields(missing);
        setError(missing.length > 0 ? `The following fields are required: ${ missing.join(', ') }` : null);
    }, [ user.firstName, user.lastName, user.phoneNumber ]);

    // MARK: Update edit effect
    React.useEffect(() => {
        setEditAction(editable ? 'cancel' : 'edit');
    }, [ editable ]);

    // MARK: Validate phone effect
    React.useEffect(() => {
        validatePhoneNumber(user.phoneNumber || '');
    }, [ user.phoneNumber ]);

    // MARK: Render
    return (
        <Accordion expanded={ editable } onChange={ handleOnEdit } sx={ {
            width: '100%',
            background: 'none',
            boxShadow: 'none',
            border: editable ? '2px solid #f0e356ff' : 'none', '&:before': { display: 'none' }
        } }>
            { editable && (userIsAdmin || userIsSelf) ? (
                <AccordionDetails>
                    <div className={ styles.form }>
                        {/* MARK: Personal Information */ }
                        <div className={ styles.formSection }>
                            <h3>Personal Information</h3>
                            <div className={ styles.formRow }>
                                <div className={ styles.formField }>
                                    <label htmlFor="firstName">First Name</label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        autoComplete='given-name'
                                        required
                                        value={ user.firstName || '' }
                                        onChange={ handleOnChange }
                                        className={ requiredFields.includes('firstName') ? styles.required : '' }
                                    />

                                    { requiredFields.includes('firstName') && <Alert severity="error" variant='filled'>First name is required.</Alert> }
                                </div>
                                <div className={ styles.formField }>
                                    <label htmlFor="lastName">Last Name</label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        autoComplete='family-name'
                                        required
                                        value={ user.lastName || '' }
                                        onChange={ handleOnChange }
                                        className={ requiredFields.includes('lastName') ? styles.required : '' }
                                    />

                                    { requiredFields.includes('lastName') && <Alert severity="error" variant='filled'>Last name is required.</Alert> }
                                </div>
                            </div>
                        </div>

                        {/* MARK: Contact Information */ }
                        <div className={ styles.formSection }>
                            <h3>Contact Information</h3>
                            <div className={ styles.formRow }>
                                <div className={ styles.formField }>
                                    <label htmlFor="phoneNumber">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        autoComplete='tel'
                                        required
                                        value={ user.phoneNumber || '' }
                                        onChange={ handleOnChange }
                                        className={ requiredFields.includes('phoneNumber') ? styles.required : !phoneIsValid ? styles.required : '' }
                                    />

                                    { requiredFields.includes('phoneNumber') && <Alert severity="error" variant='filled'>Phone number is invalid.</Alert> }

                                </div>

                                <div className={ styles.formField }>
                                    <label htmlFor="email">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        autoComplete='email'
                                        value={ user.email }
                                        readOnly={ true }
                                        className={ `${ styles.readOnly } ${ styles.verified }` }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MARK: Actions */ }
                    <AccordionActions sx={ { mt: 2 } }>
                        <Button variant="outlined" color="warning" onClick={ handleOnCancel }>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={ handleOnEdit } disabled={ ((userIsSelf || (!userIsSelf && userIsAdmin)) && (editAction === "cancel")) }>Save</Button>
                    </AccordionActions>

                    { ( error || requiredFields.length > 0 ) && <Alert severity="error" variant='filled'>{ error || `The following fields are required: ${ requiredFields.join(', ') }` }</Alert> }
                </AccordionDetails>
            ) : (
                <AccordionSummary
                    expandIcon={ !editable ? (userIsAdmin || userIsSelf) ? <Edit color="primary" /> : (userPrivacyInfo && <Visibility color="primary" />) : (!userIsAdmin || !userIsSelf || !userPrivacyInfo) ? <VisibilityOff color="primary" /> : null }
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <div className={ `${ styles.info } ${ user?.disabled ? styles.disabled : '' }` }>
                        <div className={ styles.details }>
                            <span className={ `${ !user?.displayName?.trim() ? styles.missing : '' }` }>{ `${ user?.displayName?.trim() || 'No Display Name' }` }</span>
                            <span>{ (userIsAdmin || userIsSelf || userPrivacyInfo?.phoneNumber) ? user?.phoneNumber || 'Phone' : '' }</span>
                            <span className={ `${ user?.emailVerified ? styles.verified : '' }` }>{ (userIsAdmin || userIsSelf || userPrivacyInfo?.email) ? user?.email || 'Email' : '' }{ user?.emailVerified && (userIsAdmin || userIsSelf) && <Check fontSize='small' /> }</span>
                            <span>&nbsp;</span>
                        </div>
                    </div>
                </AccordionSummary>
            )}
        </Accordion >
    );
};